import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getMonthRange } from "@/lib/dateUtils";

interface MonthSelectorProps {
  dateRange: { from: Date | undefined; to: Date | undefined };
  onMonthSelect: (year: number, month: number) => void;
}

export const MonthSelector: React.FC<MonthSelectorProps> = ({ dateRange, onMonthSelect }) => {
  const today = new Date();
  const currentYear = today.getUTCFullYear();
  const currentMonth = today.getUTCMonth() + 1;

  // Generate every month from the current month back to the earliest tracked
  // month (July 2025) so no month is ever skipped. The two most recent entries
  // are labelled "Este mês" / "Mês passado"; the rest show "Mês Ano".
  const MONTH_NAMES_PT = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
  ];
  const EARLIEST_YEAR = 2025;
  const EARLIEST_MONTH = 7;

  const uniqueOptions: { year: number; month: number; label: string }[] = [];
  {
    let y = currentYear;
    let m = currentMonth;
    let index = 0;
    while (y > EARLIEST_YEAR || (y === EARLIEST_YEAR && m >= EARLIEST_MONTH)) {
      const label = index === 0 ? "Este mês" : index === 1 ? "Mês passado" : `${MONTH_NAMES_PT[m - 1]} ${y}`;
      uniqueOptions.push({ year: y, month: m, label });
      index++;
      m--;
      if (m === 0) {
        m = 12;
        y--;
      }
    }
  }

  // Find currently selected value
  const dateStart = dateRange.from?.toISOString().split("T")[0];
  const dateEnd = dateRange.to?.toISOString().split("T")[0];
  const activeValue = uniqueOptions.find(({ year, month }) => {
    const mr = getMonthRange(year, month);
    return dateStart === mr.start && dateEnd === mr.end;
  });

  const selectedKey = activeValue ? `${activeValue.year}-${activeValue.month}` : `${currentYear}-${currentMonth}`;

  return (
    <Select
      value={selectedKey}
      onValueChange={(val) => {
        const [y, m] = val.split("-").map(Number);
        onMonthSelect(y, m);
      }}
    >
      <SelectTrigger className="w-auto gap-2 border-white/10 bg-white/5 hover:bg-white/10 transition-colors text-sm font-medium rounded-full px-4">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {uniqueOptions.map(({ year, month, label }) => (
          <SelectItem key={`${year}-${month}`} value={`${year}-${month}`}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
